import classnames from 'classnames';
import BalancedListAdvice from 'high-school-student/college-earnings/components/balanced-list-advice';
import {compact, groupBy, map} from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import ApplicationStatusCarousel from 'shared/application-management/components/application-status-carousel';
import MyListSection from 'shared/application-management/components/my-list-section';
import {
  ACCEPTED,
  APPLIED,
  ENROLLED,
  NOT_INTERESTED,
  PLANNING_TO_APPLY,
  PLANNING_TO_ENROLL,
  STATUS_LABELS,
  UNDECIDED,
} from 'shared/college-application-status/utils';
import styles from 'src/components/CollegeSearch/MyList/styles.module.scss';
import {RecommendationsService} from 'src/services/RecommendationsService';
import {EducationPhase, UserType} from 'src/types/enums';
import req from 'shared/req';
import {currentSchoolYear, secondHalfOfSchoolYear} from '../../../../shared/utils';

export default class MyList extends React.Component {
  state = {
    totalCredits: 0,
  };

  static propTypes = {
    receiveEarnings: PropTypes.func.isRequired,
    getCollegeAppStatuses: PropTypes.func.isRequired,
    getUserInfo: PropTypes.func.isRequired,
    fetchFeatureFlags: PropTypes.func.isRequired,
    fetchAdmissionLikelihoodScores: PropTypes.func.isRequired,
    collegeApplicationStatuses: PropTypes.object.isRequired,
    earnings: PropTypes.array.isRequired,
    admissionLikelihoodTierCounts: PropTypes.object,
    setCollegeAppStatus: PropTypes.func,
    receiveEarningsOrder: PropTypes.func,
    updateEarningsOrder: PropTypes.func,
    studentId: PropTypes.string,
  };

  state = {
    isLoading: false,
    recommendationsOnline: false,
  };

  componentDidMount() {
    this.loadData();
  }

  checkRecommendationOnline = () => {
    RecommendationsService.getState().then((serviceState) =>
      this.setState({
        recommendationsOnline: !serviceState.disableAdmissionLikelihood,
      })
    );
  };

  isBalancedListInfoAvailable = () => {
    const {totalCredits, graduationYear} = this.state;
    if (!totalCredits || !graduationYear) {
      return false;
    }

    if (totalCredits < 10) {
      return false;
    }

    if (graduationYear === currentSchoolYear()) {
      return true;
    }

    return graduationYear === currentSchoolYear() + 1 && secondHalfOfSchoolYear();
  };

  juniorOrSenior = () => {
    const {educationPhase, graduationYear} = this.state;

    if (educationPhase !== EducationPhase.HIGH_SCHOOL) {
      return false;
    }

    return graduationYear - currentSchoolYear() <= 1;
  };

  refreshAdmissionLikelihoodScores = async () => {
    const props = this.props;
    if (
      this.state.recommendationsOnline &&
      this.state.educationPhase === UserType.HS_STUDENT &&
      this.isBalancedListInfoAvailable()
    ) {
      const collegeIds = map(props.earnings, '_id');
      return props.fetchAdmissionLikelihoodScores(collegeIds);
    }
  };

  loadData = async () => {
    const props = this.props;
    this.setState({isLoading: true});
    await this.checkRecommendationOnline();
    await req({
      method: 'get',
      url: '/v1/users/me?fields=totalCredits,highSchoolInfo,educationPhase',
    }).then((response) => {
      this.setState({
        totalCredits: response.totalCredits,
        graduationYear: response.highSchoolInfo?.graduationYear,
        educationPhase: response.educationPhase,
      });
    });

    const admissionLikelihoodPromise = Promise.all([
      props.getUserInfo(this.state.educationPhase),
      props.receiveEarnings(),
      props.fetchFeatureFlags(),
    ]).then(this.refreshAdmissionLikelihoodScores);

    Promise.all([admissionLikelihoodPromise, props.getCollegeAppStatuses()]).finally(() => {
      this.setState({isLoading: false});
    });
  };

  render() {
    const {
      earnings,
      setCollegeAppStatus,
      collegeApplicationStatuses,
      admissionLikelihoodTierCounts,
      receiveEarningsOrder,
      updateEarningsOrder,
      studentId,
    } = this.props;

    const {educationPhase} = this.state;

    const juniorSeniorOrTransfer = this.juniorOrSenior() || educationPhase === UserType.CC_STUDENT;

    // Add applicationStatus to each college earnings entry
    const augmentedEarnings = map(earnings, (earning) => {
      const applicationStatus = collegeApplicationStatuses[earning._id];
      return {...earning, applicationStatus};
    });

    const FOLLOWING = 'following';

    // Group by application status, or default to FOLLOWING if empty
    const groupedEarnings = groupBy(augmentedEarnings, ({applicationStatus}) =>
      applicationStatus ? applicationStatus.status : FOLLOWING
    );

    const orderedGroups = [
      FOLLOWING,
      PLANNING_TO_APPLY,
      APPLIED,
      ACCEPTED,
      PLANNING_TO_ENROLL,
      ENROLLED,
      UNDECIDED,
      NOT_INTERESTED,
    ];

    const statusLabels = {
      ...STATUS_LABELS,
      [FOLLOWING]: juniorSeniorOrTransfer ? 'No Application Status' : 'Followed Colleges',
    };

    const sections = map(orderedGroups, (groupName) => {
      const earningsGroup = groupedEarnings[groupName];

      let emptyContent = null;

      if (groupName === PLANNING_TO_APPLY && juniorSeniorOrTransfer) {
        emptyContent = (
          <p>
            Add colleges you&apos;re following or <a href="/colleges">discover more colleges</a>.
          </p>
        );
      }

      if (earningsGroup || emptyContent) {
        const name = statusLabels[groupName];
        const isApplyButtonShown = groupName === PLANNING_TO_APPLY;

        return (
          <MyListSection
            key={groupName}
            name={name}
            collegeEarnings={earningsGroup}
            setCollegeAppStatus={setCollegeAppStatus}
            isApplicationStatusMenuShown={juniorSeniorOrTransfer}
            reorderingEnabled={!juniorSeniorOrTransfer}
            isApplyButtonShown={isApplyButtonShown}
            isDiscoverCollegesButtonShown={groupName === FOLLOWING && juniorSeniorOrTransfer}
            emptyContent={emptyContent}
            receiveEarningsOrder={receiveEarningsOrder}
            studentId={studentId}
            updateEarningsOrder={updateEarningsOrder}
            isBalancedListInfoAvailable={this.isBalancedListInfoAvailable()}
          />
        );
      }
    });

    const carouselColleges = groupedEarnings[FOLLOWING];
    const isCarouselVisible = carouselColleges && juniorSeniorOrTransfer;
    const totalCredits = this.state.totalCredits || 0;
    const showListAdvice = this.isBalancedListInfoAvailable() && this.state.recommendationsOnline;

    return (
      <div className={styles.container}>
        {isCarouselVisible && (
          <div className={styles.carouselHeader}>
            <ApplicationStatusCarousel
              colleges={carouselColleges}
              setCollegeAppStatus={setCollegeAppStatus}
            />
          </div>
        )}
        <div className={styles.contentContainer}>
          <div className={styles.content}>
            {showListAdvice && (
              <div className={classnames(styles.sidebar, 'my-list-sidebar')}>
                <BalancedListAdvice
                  totalCredits={totalCredits}
                  admissionLikelihoodTierCounts={admissionLikelihoodTierCounts}
                />
              </div>
            )}
            <div className={styles.main}>
              {this.state.isLoading ? <MyListSection.Loading /> : compact(sections)}
            </div>
          </div>
        </div>
      </div>
    );
  }
}
